import { useState, useCallback } from 'react';
import { ElementInfo } from '../../types/element.types';
import { useElementUpdates } from '../../hooks/dataUpdates/useElementUpdates';
import { ElementFindReplaceData } from './useMultiColumnFindReplace';

export interface ElementFindReplacePosition {
  top: number;
  left: number;
}

export const useElementFindReplace = (
  getElements: () => ElementInfo[],
  breakdownId?: string
) => {
  const { updateElementsData } = useElementUpdates();
  const [findReplaceVisible, setFindReplaceVisible] = useState(false);
  const [findReplacePosition, setFindReplacePosition] = useState<ElementFindReplacePosition>({ top: 0, left: 0 });

  const handleCloseFindReplace = useCallback(() => {
    setFindReplaceVisible(false);
  }, []);

  const getElementColumnData = useCallback((): ElementFindReplaceData[] => {
    const items = getElements();
    return items.map(item => ({
      elementName: item.elementName || '',
      elementAbbr: item.elementAbbr || '',
      elementType: item.elementType,
    }));
  }, [getElements]);

  const handleFindReplaceUpdate = useCallback(async (updatedData: ElementFindReplaceData[]) => {
    const elements = getElements();
    if (!breakdownId) {
      console.error('查找替换失败：缺少 breakdownId');
      return;
    }

    const n = Math.min(updatedData.length, elements.length);
    const finalState: Array<{ elementName: string; elementAbbr: string }> = [];
    for (let i = 0; i < n; i++) {
      const item = elements[i];
      const data = updatedData[i];
      finalState.push({
        elementName: (data?.elementName ?? item?.elementName ?? '').trim(),
        elementAbbr: (data?.elementAbbr ?? item?.elementAbbr ?? '').trim(),
      });
    }

    const ensureUnique = (
      value: string,
      field: 'elementName' | 'elementAbbr',
      excludeIndex: number
    ): string => {
      let result = value;
      const isDuplicate = (r: string) =>
        finalState.some((s, idx) => idx !== excludeIndex && s[field] === r);
      while (isDuplicate(result)) {
        result += "ⁱ";
      }
      return result;
    };

    const batchUpdates: Array<{ elementId: string; data: Partial<{ elementName: string; elementAbbr: string }> }> = [];
    for (let i = 0; i < n; i++) {
      const item = elements[i];
      const data = updatedData[i];
      if (!item || !data) continue;

      const origName = (item.elementName || '').trim();
      const origAbbr = (item.elementAbbr || '').trim();
      const newName = (data.elementName ?? '').trim();
      const newAbbr = (data.elementAbbr ?? '').trim();
      const hasNameChange = origName !== newName;
      const hasAbbrChange = origAbbr !== newAbbr;

      if (hasNameChange || hasAbbrChange) {
        let finalName = finalState[i].elementName;
        let finalAbbr = finalState[i].elementAbbr;
        if (hasNameChange) {
          finalName = ensureUnique(finalName, 'elementName', i);
          finalState[i].elementName = finalName;
        }
        if (hasAbbrChange) {
          finalAbbr = ensureUnique(finalAbbr, 'elementAbbr', i);
          finalState[i].elementAbbr = finalAbbr;
        }
        batchUpdates.push({
          elementId: item.elementID,
          data: {
            ...(hasNameChange && { elementName: finalName }),
            ...(hasAbbrChange && { elementAbbr: finalAbbr }),
          },
        });
      }
    }

    if (batchUpdates.length > 0) {
      await updateElementsData(breakdownId, batchUpdates);
      console.log('批量更新场次元素成功:', batchUpdates.length, '个元素');
    }
  }, [getElements, breakdownId, updateElementsData]);

  return {
    findReplaceVisible,
    findReplacePosition,
    setFindReplaceVisible,
    setFindReplacePosition,
    handleCloseFindReplace,
    getElementColumnData,
    handleFindReplaceUpdate,
  };
};
